import { Suspense } from 'react';

import { Box, Stack } from '@iwoca/orion';
import { SafeHTML, Skeleton } from '@ui';

import { ErrorBoundary } from '../../../../../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../../../../../modals/fallbackErrorModal/FallbackErrorModal';
import { ScrollPanel } from '../../../../../../../ui/scrollPanel/ScrollPanel';
import { useGetUnsignedDocument } from '../../hooks/useGetUnsignedDocument';

type TDocumentViewerProps = {
    documentId: string;
    onReadToEnd: () => void;
};

export const DocumentViewer = (props: TDocumentViewerProps) => {
    return (
        // ORION-113-GAP token: no surface for the neutral grey structure-95
        <Box
            border="ready"
            radius="nested"
            overflow="hidden"
            className="bg-structure-95"
        >
            <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                <Suspense fallback={<DocumentSkeleton />}>
                    <DocumentViewerContent {...props} />
                </Suspense>
            </ErrorBoundary>
        </Box>
    );
};

const DocumentViewerContent = ({
    documentId,
    onReadToEnd
}: TDocumentViewerProps) => {
    const html = useGetUnsignedDocument(documentId);

    return (
        <ScrollPanel
            type="div"
            enabled
            onBottomVisible={onReadToEnd}
            // ORION-113-GAP component: ScrollPanel has to be the scroll container and there's no height token, so its fixed height and scrolling stay as classes
            className="h-[460px] overflow-y-auto"
            data-testid="document-viewer"
        >
            <Box padding="nested">
                <SafeHTML html={html} />
            </Box>
        </ScrollPanel>
    );
};

const DocumentSkeleton = () => (
    // ORION-113-GAP token: no height token for the 460px document viewer
    <Stack
        padding="nested"
        overflow="hidden"
        className="h-[460px]"
        data-testid="document-viewer-loading"
    >
        {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="h-xl w-full" />
        ))}
    </Stack>
);
